/**
 * Everything the Meet & Assist inquiry form needs to know about each service,
 * kept as plain data so the components only have to read it.
 *
 * The form has two levels of tabs:
 *   1. Service group -> Airport or Station
 *   2. Journey       -> Arrival / Departure (/ Connecting Flights for airports)
 * Each group + journey pair is one "service" with its own key below.
 */
import {
  HANEDA_AIRPORT_PLACE_ID,
  NARITA_AIRPORT_PLACE_ID,
  SHINAGAWA_STATION_PLACE_ID,
  SHINJUKU_STATION_PLACE_ID,
  TOKYO_STATION_PLACE_ID,
} from "../../../utils/meetAssist";

/** Where the services page "Inquire Now" buttons send the guest. */
export const MEET_ASSIST_INQUIRY_PATH = "/services/meet-assist-inquiry";

/**
 * Hero photo. Kept on S3 next to the other services page photos.
 * Upload: public_folder/cms-image/cms-services/meet-assist-inquiry/banner.webp
 */
export const HERO_IMAGE_URL =
  "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-services/meet-assist-inquiry/banner.webp";

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export const GROUP_AIRPORT = "airport";
export const GROUP_STATION = "station";

export const JOURNEY_ARRIVAL = "arrival";
export const JOURNEY_DEPARTURE = "departure";
export const JOURNEY_CONNECTING = "connecting";

/** The journeys each group offers, in tab order. */
export const JOURNEYS_BY_GROUP = {
  [GROUP_AIRPORT]: [JOURNEY_ARRIVAL, JOURNEY_DEPARTURE, JOURNEY_CONNECTING],
  [GROUP_STATION]: [JOURNEY_ARRIVAL, JOURNEY_DEPARTURE],
};

/**
 * Keep these keys in sync with SERVICE_DEFINITIONS in
 * routes/controllers/MeetAssistInquiry.js -- the server validates against them.
 */
export const SERVICE_KEYS = {
  airportArrival: "airportArrival",
  airportDeparture: "airportDeparture",
  airportConnecting: "airportConnecting",
  stationArrival: "stationArrival",
  stationDeparture: "stationDeparture",
};

export const getServiceKey = (group, journey) =>
  `${group}${journey.charAt(0).toUpperCase()}${journey.slice(1)}`;

// ---------------------------------------------------------------------------
// Service options (tiers)
// ---------------------------------------------------------------------------

export const TIER_STANDARD = "standard";
export const TIER_VIP = "vip";

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

/** How each schedule field is drawn. Labels live in the translation files. */
export const FIELD_TYPE_FLIGHT = "flight";
export const FIELD_TYPE_DATE = "date";
export const FIELD_TYPE_TIME = "time";

export const SCHEDULE_FIELD_TYPES = {
  arrivalFlightNumber: FIELD_TYPE_FLIGHT,
  arrivalDate: FIELD_TYPE_DATE,
  arrivalTime: FIELD_TYPE_TIME,
  departureFlightNumber: FIELD_TYPE_FLIGHT,
  departureDate: FIELD_TYPE_DATE,
  departureTime: FIELD_TYPE_TIME,
  meetingTime: FIELD_TYPE_TIME,
};

/**
 * Per-service setup.
 *  - tiers:            options the guest may pick; one tier = shown as a fixed card
 *  - scheduleRows:     date/time/flight inputs, one array per row
 *  - hasPriorLocation: departures ask where the guest is coming from, and when
 *  - hasCarAndDriver:  whether staff hand the guest over to a car
 *  - serviceTimeField: the time that decides the night surcharge
 *  - summaryFields:    rows of the summary card after the passenger counts
 *  - disclaimerKeys:   bullet points in the grey disclaimer box
 */
export const SERVICE_CONFIG = {
  [SERVICE_KEYS.airportArrival]: {
    group: GROUP_AIRPORT,
    journey: JOURNEY_ARRIVAL,
    tiers: [TIER_STANDARD, TIER_VIP],
    scheduleRows: [["arrivalFlightNumber", "arrivalDate", "arrivalTime"]],
    hasPriorLocation: false,
    hasCarAndDriver: true,
    serviceTimeField: "arrivalTime",
    summaryFields: ["locationName", "terminalName", "arrivalFlightNumber", "arrivalDate", "arrivalTime"],
    disclaimerKeys: ["NightFee", "TaxiFeeArrival"],
  },
  [SERVICE_KEYS.airportDeparture]: {
    group: GROUP_AIRPORT,
    journey: JOURNEY_DEPARTURE,
    tiers: [TIER_STANDARD, TIER_VIP],
    scheduleRows: [["departureFlightNumber", "departureDate", "meetingTime"]],
    hasPriorLocation: true,
    hasCarAndDriver: true,
    serviceTimeField: "meetingTime",
    summaryFields: ["locationName", "terminalName", "departureFlightNumber", "departureDate", "meetingTime"],
    disclaimerKeys: ["NightFee", "TaxiFeeDeparture"],
  },
  [SERVICE_KEYS.airportConnecting]: {
    group: GROUP_AIRPORT,
    journey: JOURNEY_CONNECTING,
    tiers: [TIER_VIP],
    scheduleRows: [
      ["arrivalFlightNumber", "arrivalDate", "arrivalTime"],
      ["departureFlightNumber", "departureDate", "departureTime"],
    ],
    hasPriorLocation: false,
    hasCarAndDriver: false,
    serviceTimeField: "arrivalTime",
    summaryFields: [
      "locationName", "terminalName", "arrivalFlightNumber", "arrivalDate", "arrivalTime",
      "departureFlightNumber", "departureDate", "departureTime",
    ],
    disclaimerKeys: ["NightFee", "ConnectingDuration", "ConnectingStaff", "TaxiFeeConnecting"],
  },
  [SERVICE_KEYS.stationArrival]: {
    group: GROUP_STATION,
    journey: JOURNEY_ARRIVAL,
    tiers: [TIER_STANDARD],
    scheduleRows: [["arrivalDate", "arrivalTime"]],
    hasPriorLocation: false,
    hasCarAndDriver: true,
    serviceTimeField: "arrivalTime",
    summaryFields: ["locationName", "trainDetails", "arrivalDate", "arrivalTime"],
    disclaimerKeys: ["NightFee", "TaxiFeeArrival"],
  },
  [SERVICE_KEYS.stationDeparture]: {
    group: GROUP_STATION,
    journey: JOURNEY_DEPARTURE,
    tiers: [TIER_STANDARD],
    scheduleRows: [["departureDate", "meetingTime"]],
    hasPriorLocation: true,
    hasCarAndDriver: true,
    serviceTimeField: "meetingTime",
    summaryFields: ["locationName", "trainDetails", "departureDate", "meetingTime"],
    disclaimerKeys: ["NightFee", "TaxiFeeDeparture"],
  },
};

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

/**
 * Places that offer Meet & Assist, with the terminals guests can choose.
 * `labelKey`s point into the page translation file.
 */
export const LOCATIONS_BY_GROUP = {
  [GROUP_AIRPORT]: [
    {
      placeId: HANEDA_AIRPORT_PLACE_ID,
      labelKey: "Locations.Haneda",
      terminals: [
        "terminal-1-domestic",
        "terminal-2-domestic",
        "terminal-2-international",
        "terminal-3-international",
      ],
    },
    {
      placeId: NARITA_AIRPORT_PLACE_ID,
      labelKey: "Locations.Narita",
      terminals: ["terminal-1", "terminal-2", "terminal-3"],
    },
  ],
  [GROUP_STATION]: [
    { placeId: TOKYO_STATION_PLACE_ID, labelKey: "Locations.TokyoStation", terminals: [] },
    { placeId: SHINJUKU_STATION_PLACE_ID, labelKey: "Locations.ShinjukuStation", terminals: [] },
    { placeId: SHINAGAWA_STATION_PLACE_ID, labelKey: "Locations.ShinagawaStation", terminals: [] },
  ],
};

/** The design opens on Haneda Terminal 3 and Tokyo Station. */
export const DEFAULT_LOCATION = {
  [GROUP_AIRPORT]: { placeId: HANEDA_AIRPORT_PLACE_ID, terminal: "terminal-3-international" },
  [GROUP_STATION]: { placeId: TOKYO_STATION_PLACE_ID, terminal: "" },
};

export const findLocation = (group, placeId) =>
  LOCATIONS_BY_GROUP[group].find((location) => location.placeId === placeId);

// ---------------------------------------------------------------------------
// Passengers
// ---------------------------------------------------------------------------

export const MIN_ADULTS = 1;
export const MAX_PASSENGERS = 30;

/** Children are 0-11, matching "0~11 years old" on the counter. */
export const CHILD_AGE_OPTIONS = Array.from({ length: 12 }, (_, age) => age);

export const PASSENGER_TITLE_OPTIONS = ["Mr.", "Ms.", "Mrs."];

export const PICKUP_SIGN_SAME = "same";
export const PICKUP_SIGN_CUSTOM = "custom";

export const CAR_DETAILS_READY = "ready";
export const CAR_DETAILS_LATER = "later";

export const CONTACT_SAME = "same";
export const CONTACT_DIFFERENT = "different";

/** Japan is the default country for every phone field. */
export const DEFAULT_PHONE = { countryCode: "JP", dialCode: "+81", number: "" };

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** How dates read in the summary, e.g. "Aug. 11th, 2023 (FRI)". */
export const SUMMARY_DATE_FORMAT = {
  en: "MMM. Do, YYYY (ddd)",
  jp: "YYYY年M月D日(dd)",
};

/** Sent to the server and shown in the admin email, whatever the UI language. */
export const PAYLOAD_DATE_FORMAT = "YYYY-MM-DD (ddd)";
